import classNames from 'classnames/bind';
import styles from './ClassActivityInfo.module.css';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { selectUserAlias } from '../../../features/authSlice';
import {
  ALL,
  ALL_GRADE_CLASSES,
  ALL_SCHOOL_GRADES,
  CARD_CONFIGS,
  CLASS_ACTIVITY_SUFFIX,
  CLASS_AVERAGE_LEARNING_TIME,
  CLASS_NOTE_USAGE_AVERAGE_DETAILS,
  CLASS_STUDENT_ACTIVITIES,
  COLLECTED_STUDENT_AVERAGE_INFO,
  CONTENT_LABELS,
  DAILY_BOOKS_LEARNED_DETAILS,
  DAILY_LEARNING_BOOKS,
  DAILY_STUDY_COUNT,
  EMPTY_STUDENT_LIST,
  GRADE,
  GROUP,
  LAST_LOGIN_DATE,
  LEARNING_CONTENTS,
  LEARNING_TOOL_LABELS,
  LEARNING_TOOLS,
  MAX_CONCENTRATION_TIME,
  NAME,
  NOTE_USAGE_TIME,
  PRIMARY_STUDY_DAYS,
  PRIMARY_STUDY_HOURS,
  SCHOOL_NAME,
  SEARCH_RESULT_NOT_FOUND,
  SELPUB_STUDENTS_AVERAGE_CONCENTRATION_TIME,
  SELPUB_STUDENTS_AVERAGE_NOTE,
  SELPUB_STUDENTS_DAILY_BOOKS_LEARNED,
  TOTAL_STUDY_TIME,
  USER_LOGIN_ANALYSIS,
  VOLUME,
} from '../../../constants';
import { ClassOptions } from '../../../types/analysis';
import {
  FullDashboard,
  ClassLastStudyPanel,
  ClassAttendancePanel,
  KeywordPanel,
  HorizontalBarPanel,
  VerticalBarPanel,
  PlainInfoPanel,
} from '../../../components/Dashboard';
import { PanelType, PanelConfig } from '../../../types/dashboard';
import { formatCount, makeCard } from '../../../utils/dashboard';
import {
  useGetClassInitQuery,
  useGetClassSummaryMutation,
} from '../../../services/analysis';
import { useEffect, useState } from 'react';
import {
  classOptionsChanged,
  classStudentListChanged,
  classStudentListReset,
  classSummaryChanged,
  classSummaryDisabled,
  selectClassOptions,
  selectClassStudentList,
  selectClassSummary,
  studentUserIdChanged,
} from '../../../features/analysisSlice';
import Search from '../../../components/Search';
import Filter from '../../../components/Filter';
import Table from '../../../components/Table';
import { useNavigate } from 'react-router';
import { isMobile } from '../../../utils/util';
import { formatDate } from '../../../utils/date';

export default function ClassActivityInfo() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const alias = useAppSelector(selectUserAlias) ?? '';
  const summary = useAppSelector(selectClassSummary);
  const classStudentList = useAppSelector(selectClassStudentList);
  const options = useAppSelector(selectClassOptions);

  const { data: initialValues, isLoading: isLoadingInit } =
    useGetClassInitQuery();
  const [getSummary] = useGetClassSummaryMutation();

  const [newOptions, setNewOptions] = useState<ClassOptions>();
  const [selectDefault, setSelectDefault] = useState({
    key: 'totalTime',
    value: TOTAL_STUDY_TIME,
  });
  const [activePanel, setActivePanel] = useState<string | null>(null);

  const changeSummary = async (options: ClassOptions) => {
    try {
      const res = await getSummary(options);
      if ('data' in res) {
        if (res.data) {
          const { summary, classStudentList } = res.data;
          dispatch(classSummaryChanged(summary));
          dispatch(classStudentListChanged(classStudentList));

          if (!options.startDate) {
            dispatch(
              classOptionsChanged({
                startDate: formatDate(summary.regDate, 1),
                endDate: formatDate(summary.today, 1),
              })
            );
          }
        } else {
          dispatch(classSummaryDisabled());
          dispatch(classStudentListReset());
        }
      }
    } catch (error) {
      console.error('Fail to get Summary', error);
    }
  };

  const changeSearchKeyword = (keyword: string) => {
    dispatch(classOptionsChanged({ search: keyword }));
  };

  const selectTableHeader = (value: string) => {
    const entries = Object.entries(CLASS_STUDENT_ACTIVITIES);
    let key = '';

    for (const [k, val] of entries) {
      if (val === value) {
        key = k;
      }
    }
    setSelectDefault({ key, value });
  };

  useEffect(() => {
    options && setNewOptions({ ...newOptions, ...options });
  }, [options]);

  useEffect(() => {
    newOptions && changeSummary(newOptions);
  }, [newOptions]);

  useEffect(() => {
    dispatch(studentUserIdChanged(0));
    dispatch(
      classOptionsChanged({ search: '', endDate: formatDate(summary.today, 1) })
    );
  }, []);

  if (isLoadingInit || !initialValues || !options || !classStudentList)
    return null;

  const {
    commonType: { schoolTypeList, gradeTypeList },
    groupList,
  } = initialValues;

  const {
    regDate,
    today,
    total,
    timeZone,
    weekZone,
    daily,
    attention,
    note,
    lastStudy,
    lastAttendance,
    keyword,
    annotation,
    content,
  } = summary;
  const { studentCount, totalTime } = total;

  const cardValues: Record<string, { value: string; coverImage?: string }> = {
    'last-learning': {
      value: lastStudy?.title || '-',
      coverImage: lastStudy?.coverFilePath,
    },
    'last-date': {
      value:
        (lastAttendance?.lastDate && formatDate(lastAttendance.lastDate)) ||
        '-',
    },
    keyword: {
      value:
        keyword && keyword.keywordList.length
          ? keyword.keywordList[0].keyword
          : '-',
    },
    tool: { value: formatCount(annotation?.avgTotalCount) },
    content: { value: formatCount(content?.avgTotalCount) },
    'total-time': { value: totalTime || '-' },
    'time-range': { value: timeZone.best || '-' },
    day: { value: weekZone.best || '-' },
    book: { value: daily.classAvg ? `${daily.classAvg}${VOLUME}` : '-' },
    focus: { value: attention.classAvg || '-' },
    note: { value: note.classAvg || '-' },
  };

  const activityCards = CARD_CONFIGS.map(({ id, title }) => {
    const { value, coverImage } = cardValues[id];
    return makeCard(id, title, value, coverImage);
  });

  const dashboardTitle = CLASS_ACTIVITY_SUFFIX.replace('{alias}', alias);

  const dateRange = {
    startDate: newOptions?.startDate || regDate,
    endDate: newOptions?.endDate || today,
    regDate,
    today,
  };

  const handleDateChange = (startDate: string, endDate: string) => {
    dispatch(classOptionsChanged({ startDate, endDate }));
  };

  const handleGroupChange = (groupName: string, groupIndex: number) => {
    const seq = groupIndex === 0 ? null : [groupList[groupIndex - 1].seq];
    dispatch(classOptionsChanged({ seq, search: '' }));
  };

  const handleCardClick = (cardId: string) => {
    const card = activityCards.find((card) => card.id === cardId);
    if (card && card.value !== '-') {
      setActivePanel(cardId);
    }
  };

  const handleClosePanel = () => {
    setActivePanel(null);
  };

  const lastProjectListForPanel = () =>
    lastStudy?.lastProjectList?.map((student) => ({
      nm: student.nm,
      schoolNm: student.schoolNm,
      gradeTypeNm: student.gradeNm,
      groupNames: student.groupNames,
      title: student.title,
      page: student.lastPage,
    })) || [];

  const selectedGroup = options.seq
    ? groupList.find(({ seq }) => seq === options.seq?.[0])?.groupNm || ALL
    : ALL;

  const panelConfigs: PanelConfig[] = [
    {
      id: PanelType.LAST_STUDY,
      Component: ClassLastStudyPanel,
      condition: classStudentList.length > 0,
      props: {
        data: lastProjectListForPanel(),
      },
    },
    {
      id: PanelType.LAST_ATTENDANCE,
      Component: ClassAttendancePanel,
      condition: !!lastAttendance?.lastDate,
      props: {
        seq: options.seq,
        attendedDates: lastAttendance?.dateList || [],
      },
    },
    {
      id: PanelType.KEYWORD,
      Component: KeywordPanel,
      condition: keyword?.status === 1,
      props: {
        data:
          keyword?.keywordList.slice(0, 5).map((item) => ({
            rank: item.order,
            keyword: item.keyword,
          })) || [],
        studentCount: keyword?.studentCount,
      },
    },
    {
      id: PanelType.TOOL,
      Component: HorizontalBarPanel,
      condition: !!annotation?.avgTotalCount,
      props: {
        cardId: 'tool',
        title: LEARNING_TOOLS,
        data:
          annotation?.typeList.slice(0, 3).map((item) => ({
            rank: item.order,
            name: item.dispType,
            value: item.count,
          })) || [],
        totalCount: annotation?.avgTotalCount,
        topItemName: annotation?.typeList.find((item) => item.order === 1)
          ?.dispType,
        nameLabels: LEARNING_TOOL_LABELS,
        studentCount: annotation?.studentCount,
      },
    },
    {
      id: PanelType.CONTENT,
      Component: HorizontalBarPanel,
      condition: !!content?.avgTotalCount,
      props: {
        cardId: 'content',
        title: LEARNING_CONTENTS,
        data:
          content?.typeList.slice(0, 3).map((item) => ({
            rank: item.order,
            name: item.dispType,
            value: item.count,
          })) || [],
        totalCount: content?.avgTotalCount,
        topItemName: content?.typeList.find((item) => item.order === 1)
          ?.dispType,
        nameLabels: CONTENT_LABELS,
        studentCount: content?.studentCount,
      },
    },
    {
      id: PanelType.TOTAL_TIME,
      Component: PlainInfoPanel,
      condition: !!totalTime,
      props: {
        cardId: 'total-time',
        title: TOTAL_STUDY_TIME,
        data: {
          mainValue: totalTime,
          description: CLASS_AVERAGE_LEARNING_TIME.replace('{time}', totalTime),
          subtext: USER_LOGIN_ANALYSIS,
          studentCount: studentCount,
        },
      },
    },
    {
      id: PanelType.TIME_RANGE,
      Component: VerticalBarPanel,
      condition: !!timeZone.best,
      props: {
        cardId: 'time-range',
        title: PRIMARY_STUDY_HOURS,
        data:
          timeZone.timeZone.map((item) => ({
            name: item.title,
            value: item.value,
          })) || [],
        topItemName: timeZone.best,
      },
    },
    {
      id: PanelType.DAY,
      Component: VerticalBarPanel,
      condition: !!weekZone.best,
      props: {
        cardId: 'day',
        title: PRIMARY_STUDY_DAYS,
        data:
          weekZone.timeZone.map((item) => ({
            name: item.title,
            value: item.value,
          })) || [],
        topItemName: weekZone.best,
      },
    },
    {
      id: PanelType.BOOK,
      Component: PlainInfoPanel,
      condition: !!daily.classAvg,
      props: {
        cardId: 'book',
        title: DAILY_LEARNING_BOOKS,
        data: {
          mainValue: `${daily.classAvg}${VOLUME}`,
          studentCount: daily.studentCount,
          avg: daily.classAvg,
          description: DAILY_BOOKS_LEARNED_DETAILS.replace(
            '{avg}',
            `${daily.classAvg}`
          ),
          subtext: SELPUB_STUDENTS_DAILY_BOOKS_LEARNED.replace(
            '{N}',
            `${daily.totalAvg}`
          ),
        },
      },
    },
    {
      id: PanelType.FOCUS,
      Component: PlainInfoPanel,
      condition: !!attention.classAvg,
      props: {
        cardId: 'focus',
        title: MAX_CONCENTRATION_TIME,
        data: {
          mainValue: attention.classAvg,
          studentCount: attention.studentCount,
          total: attention.totalAvg,
          description: CLASS_AVERAGE_LEARNING_TIME.replace(
            '{time}',
            attention.classAvg || ''
          ),
          subtext: SELPUB_STUDENTS_AVERAGE_CONCENTRATION_TIME.replace(
            '{totalAvg}',
            attention.totalAvg
          ),
        },
      },
    },
    {
      id: PanelType.NOTE,
      Component: PlainInfoPanel,
      condition: !!note.classAvg,
      props: {
        cardId: 'note',
        title: NOTE_USAGE_TIME,
        data: {
          mainValue: note.classAvg,
          studentCount: note.studentCount,
          total: note.totalAvg,
          description: CLASS_NOTE_USAGE_AVERAGE_DETAILS.replace(
            '{time}',
            note.classAvg || ''
          ),
          subtext: SELPUB_STUDENTS_AVERAGE_NOTE.replace(
            '{time}',
            note.totalAvg
          ),
        },
      },
    },
  ];

  return (
    <>
      <FullDashboard
        title={dashboardTitle}
        cards={activityCards}
        groupList={groupList}
        selectedGroup={selectedGroup}
        dateRange={dateRange}
        dateRangeDescSuffix={COLLECTED_STUDENT_AVERAGE_INFO}
        onDateChange={handleDateChange}
        onGroupChange={handleGroupChange}
        onCardClick={handleCardClick}
        className="class-activity-info"
      >
        {activePanel &&
          panelConfigs.map(({ id, Component, condition, props }) =>
            condition && activePanel === id ? (
              <Component
                key={id}
                {...props}
                isOpen={true}
                onClose={handleClosePanel}
              />
            ) : null
          )}
      </FullDashboard>

      <div className={cx('dash_section')}>
        <div className={cx('dash_class_data')}>
          <div className={cx('dash_option')}>
            <Search
              name={'activity-student'}
              onSubmit={changeSearchKeyword}
              isBgWhite={true}
            />
            <Filter
              list={[
                {
                  name: 'schoolType',
                  values: [
                    ALL_SCHOOL_GRADES,
                    ...schoolTypeList.map(({ nm }) => nm),
                  ],
                },
                {
                  name: 'gradeType',
                  values: [
                    ALL_GRADE_CLASSES,
                    ...gradeTypeList.map(({ nm }) => nm),
                  ],
                },
              ]}
              onCheck={(checkedList) => {
                const schoolType = checkedList.schoolType?.includes(
                  ALL_SCHOOL_GRADES
                )
                  ? []
                  : schoolTypeList
                      .filter(({ nm }) => checkedList.schoolType?.includes(nm))
                      .map(({ cd }) => cd);
                const gradeType = checkedList.gradeType?.includes(
                  ALL_GRADE_CLASSES
                )
                  ? []
                  : gradeTypeList
                      .filter(({ nm }) => checkedList.gradeType?.includes(nm))
                      .map(({ cd }) => cd);

                dispatch(
                  classOptionsChanged({ ...options, schoolType, gradeType })
                );
              }}
            />
          </div>

          {classStudentList.length ? (
            <Table
              name="activity-students"
              headList={
                isMobile
                  ? [NAME, GROUP, TOTAL_STUDY_TIME]
                  : Object.values(CLASS_STUDENT_ACTIVITIES)
              }
              bodyList={classStudentList.map((data) => ({
                ...data,
                id: data.userId,
              }))}
              keyList={
                isMobile
                  ? ['nm', 'groupNames', 'totalTime']
                  : Object.keys(CLASS_STUDENT_ACTIVITIES)
              }
              {...(isMobile && {
                selectOptions: {
                  idx: 2,
                  default: {
                    key: selectDefault.key,
                    value: selectDefault.value,
                  },
                  values: [
                    TOTAL_STUDY_TIME,
                    SCHOOL_NAME,
                    GRADE,
                    LAST_LOGIN_DATE,
                    DAILY_STUDY_COUNT,
                    MAX_CONCENTRATION_TIME,
                    NOTE_USAGE_TIME,
                  ],
                  onClick: selectTableHeader,
                },
              })}
              onClickBody={(data) =>
                navigate('../student-activity-info', {
                  state: { userId: data.id },
                })
              }
            />
          ) : (
            <div className={cx('empty_upload')}>
              <p className={cx('desc')}>
                {options.search ? SEARCH_RESULT_NOT_FOUND : EMPTY_STUDENT_LIST}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
